import React from 'react';

/**
 * Premium animated SVG icons for feature cards
 * Modern SaaS-style motion animations
 */

interface AnimatedIconProps {
    className?: string;
}

// Lightning Fast - Animated lightning bolt
export const AnimatedZapIcon: React.FC<AnimatedIconProps> = ({ className = '' }) => (
    <svg 
        viewBox="0 0 64 64" 
        className={className} 
        fill="none" 
        xmlns="http://www.w3.org/2000/svg"
    >
        <defs>
            <linearGradient id="zapGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#fbbf24" />
                <stop offset="50%" stopColor="#f59e0b" />
                <stop offset="100%" stopColor="#d97706" />
            </linearGradient>
        </defs>
        <g className="animate-pulse">
            <path
                d="M32 8L20 32h12l-4 24 20-24H36l4-24z"
                fill="url(#zapGrad)"
                className="drop-shadow-lg"
            >
                <animate
                    attributeName="opacity"
                    values="1;0.7;1"
                    dur="1.5s"
                    repeatCount="indefinite"
                />
            </path>
        </g>
        <circle cx="32" cy="32" r="2" fill="#fff" opacity="0.9">
            <animate
                attributeName="r"
                values="2;4;2"
                dur="1s"
                repeatCount="indefinite"
            />
            <animate
                attributeName="opacity"
                values="0.9;0.3;0.9"
                dur="1s"
                repeatCount="indefinite"
            />
        </circle>
    </svg>
);

// Enterprise Security - Animated shield
export const AnimatedShieldIcon: React.FC<AnimatedIconProps> = ({ className = '' }) => (
    <svg 
        viewBox="0 0 64 64" 
        className={className} 
        fill="none" 
        xmlns="http://www.w3.org/2000/svg"
    >
        <defs>
            <linearGradient id="shieldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#8b5cf6" />
                <stop offset="50%" stopColor="#7c3aed" />
                <stop offset="100%" stopColor="#6d28d9" />
            </linearGradient>
        </defs>
        <g>
            <path
                d="M32 8L16 14v14c0 12 8 22 16 28 8-6 16-16 16-28V14L32 8z"
                fill="url(#shieldGrad)"
                className="drop-shadow-lg"
            >
                <animateTransform
                    attributeName="transform"
                    type="scale"
                    values="1;1.05;1"
                    dur="2s"
                    repeatCount="indefinite"
                />
            </path>
            <path
                d="M28 32l6 6 10-10"
                stroke="#fff"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
            >
                <animate
                    attributeName="stroke-dasharray"
                    values="0,100;20,100;0,100"
                    dur="2s"
                    repeatCount="indefinite"
                />
            </path>
        </g>
    </svg>
);

// Global Scale - Animated globe
export const AnimatedGlobeIcon: React.FC<AnimatedIconProps> = ({ className = '' }) => (
    <svg 
        viewBox="0 0 64 64" 
        className={className} 
        fill="none" 
        xmlns="http://www.w3.org/2000/svg"
    >
        <defs>
            <linearGradient id="globeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#3b82f6" />
                <stop offset="50%" stopColor="#2563eb" />
                <stop offset="100%" stopColor="#1d4ed8" />
            </linearGradient>
        </defs>
        <g>
            <circle
                cx="32"
                cy="32"
                r="20"
                fill="url(#globeGrad)"
                className="drop-shadow-lg"
            />
            <ellipse
                cx="32"
                cy="32"
                rx="20"
                ry="8"
                fill="none"
                stroke="#fff"
                strokeWidth="1.5"
                opacity="0.3"
            >
                <animateTransform
                    attributeName="transform"
                    type="rotate"
                    values="0 32 32;360 32 32"
                    dur="8s"
                    repeatCount="indefinite"
                />
            </ellipse>
            <path
                d="M12 32h40M32 12v40"
                stroke="#fff"
                strokeWidth="1.5"
                opacity="0.4"
            />
            <circle cx="24" cy="24" r="2" fill="#fff" opacity="0.8">
                <animate
                    attributeName="opacity"
                    values="0.8;0.3;0.8"
                    dur="2s"
                    repeatCount="indefinite"
                />
            </circle>
            <circle cx="40" cy="28" r="1.5" fill="#fff" opacity="0.8">
                <animate
                    attributeName="opacity"
                    values="0.8;0.3;0.8"
                    dur="2.5s"
                    repeatCount="indefinite"
                />
            </circle>
            <circle cx="28" cy="40" r="1.5" fill="#fff" opacity="0.8">
                <animate
                    attributeName="opacity"
                    values="0.8;0.3;0.8"
                    dur="1.8s"
                    repeatCount="indefinite"
                />
            </circle>
        </g>
    </svg>
);

// Smart Automation - Animated sparkles
export const AnimatedSparklesIcon: React.FC<AnimatedIconProps> = ({ className = '' }) => (
    <svg 
        viewBox="0 0 64 64" 
        className={className} 
        fill="none" 
        xmlns="http://www.w3.org/2000/svg"
    >
        <defs>
            <linearGradient id="sparkleGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#ec4899" />
                <stop offset="50%" stopColor="#d946ef" />
                <stop offset="100%" stopColor="#a855f7" />
            </linearGradient>
        </defs>
        <g>
            {/* Main sparkle */}
            <path
                d="M32 8l4 12 12 4-12 4-4 12-4-12-12-4 12-4 4-12z"
                fill="url(#sparkleGrad)"
                className="drop-shadow-lg"
            >
                <animateTransform
                    attributeName="transform"
                    type="rotate"
                    values="0 32 32;360 32 32"
                    dur="3s"
                    repeatCount="indefinite"
                />
            </path>
            {/* Small sparkles */}
            <circle cx="16" cy="16" r="2" fill="#fff" opacity="0.8">
                <animate
                    attributeName="opacity"
                    values="0.8;0.2;0.8"
                    dur="1.5s"
                    repeatCount="indefinite"
                />
                <animate
                    attributeName="r"
                    values="2;3;2"
                    dur="1.5s"
                    repeatCount="indefinite"
                />
            </circle>
            <circle cx="48" cy="16" r="1.5" fill="#fff" opacity="0.8">
                <animate
                    attributeName="opacity"
                    values="0.8;0.2;0.8"
                    dur="2s"
                    repeatCount="indefinite"
                />
                <animate
                    attributeName="r"
                    values="1.5;2.5;1.5"
                    dur="2s"
                    repeatCount="indefinite"
                />
            </circle>
            <circle cx="16" cy="48" r="1.5" fill="#fff" opacity="0.8">
                <animate
                    attributeName="opacity"
                    values="0.8;0.2;0.8"
                    dur="1.8s"
                    repeatCount="indefinite"
                />
                <animate
                    attributeName="r"
                    values="1.5;2.5;1.5"
                    dur="1.8s"
                    repeatCount="indefinite"
                />
            </circle>
            <circle cx="48" cy="48" r="2" fill="#fff" opacity="0.8">
                <animate
                    attributeName="opacity"
                    values="0.8;0.2;0.8"
                    dur="2.2s"
                    repeatCount="indefinite"
                />
                <animate
                    attributeName="r"
                    values="2;3;2"
                    dur="2.2s"
                    repeatCount="indefinite"
                />
            </circle>
        </g>
    </svg>
);

// Seamless Integration - Animated connected nodes (compact)
export const AnimatedLayersIcon: React.FC<AnimatedIconProps> = ({ className = '' }) => (
    <svg 
        viewBox="0 0 64 64" 
        className={className} 
        fill="none" 
        xmlns="http://www.w3.org/2000/svg"
    >
        <defs>
            <linearGradient id="integrationGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#10b981" />
                <stop offset="50%" stopColor="#059669" />
                <stop offset="100%" stopColor="#047857" />
            </linearGradient>
        </defs>
        <g>
            {/* Connection lines - shorter and closer */}
            <path
                d="M20 32 L32 24 L44 32"
                stroke="url(#integrationGrad)"
                strokeWidth="1.5"
                strokeLinecap="round"
                fill="none"
                opacity="0.5"
            >
                <animate
                    attributeName="stroke-dasharray"
                    values="0,100;40,100;0,100"
                    dur="2s"
                    repeatCount="indefinite"
                />
            </path>
            <path
                d="M20 32 L32 40 L44 32"
                stroke="url(#integrationGrad)"
                strokeWidth="1.5"
                strokeLinecap="round"
                fill="none"
                opacity="0.5"
            >
                <animate
                    attributeName="stroke-dasharray"
                    values="0,100;40,100;0,100"
                    dur="2.5s"
                    repeatCount="indefinite"
                />
            </path>
            
            {/* Left node - smaller */}
            <circle
                cx="20"
                cy="32"
                r="5"
                fill="url(#integrationGrad)"
                className="drop-shadow-lg"
            >
                <animateTransform
                    attributeName="transform"
                    type="scale"
                    values="1;1.08;1"
                    dur="2s"
                    repeatCount="indefinite"
                />
            </circle>
            <circle cx="20" cy="32" r="2" fill="#fff" opacity="0.9">
                <animate
                    attributeName="opacity"
                    values="0.9;0.5;0.9"
                    dur="2s"
                    repeatCount="indefinite"
                />
            </circle>
            
            {/* Center top node - smaller */}
            <circle
                cx="32"
                cy="24"
                r="6"
                fill="url(#integrationGrad)"
                className="drop-shadow-lg"
            >
                <animateTransform
                    attributeName="transform"
                    type="scale"
                    values="1;1.1;1"
                    dur="2.2s"
                    repeatCount="indefinite"
                />
            </circle>
            <circle cx="32" cy="24" r="2.5" fill="#fff" opacity="0.9">
                <animate
                    attributeName="opacity"
                    values="0.9;0.5;0.9"
                    dur="2.2s"
                    repeatCount="indefinite"
                />
            </circle>
            
            {/* Right node - smaller */}
            <circle
                cx="44"
                cy="32"
                r="5"
                fill="url(#integrationGrad)"
                className="drop-shadow-lg"
            >
                <animateTransform
                    attributeName="transform"
                    type="scale"
                    values="1;1.08;1"
                    dur="2.3s"
                    repeatCount="indefinite"
                />
            </circle>
            <circle cx="44" cy="32" r="2" fill="#fff" opacity="0.9">
                <animate
                    attributeName="opacity"
                    values="0.9;0.5;0.9"
                    dur="2.3s"
                    repeatCount="indefinite"
                />
            </circle>
            
            {/* Bottom center node - smaller */}
            <circle
                cx="32"
                cy="40"
                r="5"
                fill="url(#integrationGrad)"
                className="drop-shadow-lg"
            >
                <animateTransform
                    attributeName="transform"
                    type="scale"
                    values="1;1.08;1"
                    dur="2.1s"
                    repeatCount="indefinite"
                />
            </circle>
            <circle cx="32" cy="40" r="2" fill="#fff" opacity="0.9">
                <animate
                    attributeName="opacity"
                    values="0.9;0.5;0.9"
                    dur="2.1s"
                    repeatCount="indefinite"
                />
            </circle>
        </g>
    </svg>
);

// 24/7 Support - Animated headphones
export const AnimatedHeadphonesIcon: React.FC<AnimatedIconProps> = ({ className = '' }) => (
    <svg 
        viewBox="0 0 64 64" 
        className={className} 
        fill="none" 
        xmlns="http://www.w3.org/2000/svg"
    >
        <defs>
            <linearGradient id="headphonesGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#06b6d4" />
                <stop offset="50%" stopColor="#0891b2" />
                <stop offset="100%" stopColor="#0e7490" />
            </linearGradient>
        </defs>
        <g>
            {/* Left ear cup */}
            <ellipse
                cx="20"
                cy="40"
                rx="12"
                ry="16"
                fill="url(#headphonesGrad)"
                className="drop-shadow-lg"
            >
                <animateTransform
                    attributeName="transform"
                    type="scale"
                    values="1;1.05;1"
                    dur="2s"
                    repeatCount="indefinite"
                />
            </ellipse>
            {/* Right ear cup */}
            <ellipse
                cx="44"
                cy="40"
                rx="12"
                ry="16"
                fill="url(#headphonesGrad)"
                className="drop-shadow-lg"
            >
                <animateTransform
                    attributeName="transform"
                    type="scale"
                    values="1;1.05;1"
                    dur="2.2s"
                    repeatCount="indefinite"
                />
            </ellipse>
            {/* Headband */}
            <path
                d="M20 24 Q32 16 44 24"
                stroke="url(#headphonesGrad)"
                strokeWidth="4"
                fill="none"
                strokeLinecap="round"
            >
                <animate
                    attributeName="stroke-dasharray"
                    values="0,100;50,100;0,100"
                    dur="3s"
                    repeatCount="indefinite"
                />
            </path>
            {/* Sound waves */}
            <path
                d="M8 40 Q12 36 16 40 Q20 44 24 40"
                stroke="#fff"
                strokeWidth="2"
                fill="none"
                opacity="0.6"
            >
                <animate
                    attributeName="opacity"
                    values="0.6;1;0.6"
                    dur="1.5s"
                    repeatCount="indefinite"
                />
            </path>
            <path
                d="M40 40 Q44 36 48 40 Q52 44 56 40"
                stroke="#fff"
                strokeWidth="2"
                fill="none"
                opacity="0.6"
            >
                <animate
                    attributeName="opacity"
                    values="0.6;1;0.6"
                    dur="1.7s"
                    repeatCount="indefinite"
                />
            </path>
        </g>
    </svg>
);

